import { Loader2, X } from 'lucide-react';
/**
 * Sub-spec 08 Plan 3 §3.2 — full scheduling drawer launched from the reports
 * page. Posts to `/api/scheduled-reports`.
 */
import { useEffect, useState } from 'react';

interface Props {
  reportType: string;
  reportTitle: string;
  paramsJson: string;
  onClose: () => void;
}

interface TeamMember {
  membershipId: string;
  userId: string;
  email: string;
  displayName: string | null;
}

const HOURS: ReadonlyArray<{ value: number; label: string }> = Array.from(
  { length: 24 },
  (_, i) => ({
    value: i,
    label: `${String(i).padStart(2, '0')}:00`,
  }),
);

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

export function ScheduleReportDrawer({ reportType, reportTitle, paramsJson, onClose }: Props) {
  const [schedule, setSchedule] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [sendHour, setSendHour] = useState<number>(9);
  const [dayOfWeek, setDayOfWeek] = useState<number>(1);
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadTeam() {
      try {
        const res = await fetch('/api/team', { credentials: 'include' });
        if (!res.ok) return;
        const body = (await res.json()) as { members?: TeamMember[] };
        if (!cancelled && Array.isArray(body.members)) setTeam(body.members);
      } catch {
        // Silent — drawer still works (empty list), user just can't pick.
      }
    }
    void loadTeam();
    return () => {
      cancelled = true;
    };
  }, []);

  function toggleRecipient(id: string) {
    setRecipients((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  }

  async function handleSave() {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const body: Record<string, unknown> = {
        reportType,
        paramsJson,
        schedule,
        sendHour,
        recipientMembershipIds: recipients,
      };
      if (schedule === 'weekly') body.dayOfWeek = dayOfWeek;
      if (schedule === 'monthly') body.dayOfMonth = dayOfMonth;

      const res = await fetch('/api/scheduled-reports', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const respBody = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) {
        const issues = Array.isArray(respBody.issues)
          ? (respBody.issues as string[]).join(', ')
          : null;
        setError(
          issues ??
            (typeof respBody.error === 'string' ? respBody.error : `Failed (${res.status})`),
        );
        return;
      }
      setSuccess('Scheduled — you will receive the first email at the next matching local hour.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-label="Schedule report"
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-foreground/30"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-md flex-col bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-base font-medium tracking-tight">Schedule report</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">{reportTitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-8 place-items-center rounded-md text-muted-foreground press hover:bg-secondary"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex flex-col gap-4">
            <div>
              <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Frequency
              </label>
              <div className="flex gap-1.5">
                {(['daily', 'weekly', 'monthly'] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setSchedule(opt)}
                    className={`rounded-md border px-3 py-1.5 text-sm press capitalize ${
                      schedule === opt
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-surface hover:bg-secondary'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label
                htmlFor="schedule-hour"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Send at (account local time)
              </label>
              <select
                id="schedule-hour"
                value={sendHour}
                onChange={(e) => setSendHour(Number(e.target.value))}
                className="num h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
              >
                {HOURS.map((h) => (
                  <option key={h.value} value={h.value}>
                    {h.label}
                  </option>
                ))}
              </select>
            </div>

            {schedule === 'weekly' && (
              <div>
                <label
                  htmlFor="schedule-dow"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Day of week
                </label>
                <select
                  id="schedule-dow"
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(Number(e.target.value))}
                  className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                >
                  {DAYS_OF_WEEK.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {schedule === 'monthly' && (
              <div>
                <label
                  htmlFor="schedule-dom"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Day of month (1-28)
                </label>
                <input
                  id="schedule-dom"
                  type="number"
                  min={1}
                  max={28}
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(Number(e.target.value))}
                  className="num h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                />
              </div>
            )}

            <div>
              <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Recipients
              </label>
              {team.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Loading staff list — or no teammates configured yet.
                </p>
              ) : (
                <ul className="flex flex-col gap-1 rounded-md border border-border bg-surface p-2 max-h-60 overflow-y-auto">
                  {team.map((m) => (
                    <li key={m.membershipId}>
                      <label className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-secondary/50">
                        <input
                          type="checkbox"
                          checked={recipients.includes(m.membershipId)}
                          onChange={() => toggleRecipient(m.membershipId)}
                        />
                        <span>{m.displayName ?? m.email}</span>
                        {m.displayName && (
                          <span className="text-xs text-muted-foreground">{m.email}</span>
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          {error && <span className="mr-auto text-sm text-red-600">{error}</span>}
          {success && <span className="mr-auto text-sm text-emerald-700">{success}</span>}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm press hover:bg-secondary"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting || recipients.length === 0 || Boolean(success)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {submitting && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
            Save schedule
          </button>
        </footer>
      </div>
    </div>
  );
}
